import { useCallback, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAppDispatch, useAppSelector } from '@/app/store';
import { setAuthData, clearAuth, setLoading, setError } from '@/features/auth';
import {
  fetchUserOrganisations,
  setUserOrganisation,
  logout as logoutApi,
} from '../api/authApi';
import {
  getAuthStorage,
  setAuthStorage,
  clearUserCookies,
  getUserCookies,
} from '@/shared/utils/storage';
import { redirectToLogin } from '@/shared/api/axios.config';
import { getApiErrorMessage } from '@/shared/utils/apiError';

interface JwtPayload {
  data?: {
    user?: string;
    user_id?: string;
  };
  exp?: number;
  iat?: number;
}

// Extract user email from JWT token
function getUserEmailFromToken(token: string): string {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.data?.user || 'Admin User';
  } catch {
    return 'Admin User';
  }
}

// Extract user_id from JWT token of the currently logged-in admin
export function getCurrentUserIdFromStorage(): string | null {
  const stored = getAuthStorage();
  const token = stored?.org_access_token;
  if (!token) return null;
  try {
    return jwtDecode<JwtPayload>(token).data?.user_id || null;
  } catch {
    return null;
  }
}

export function useAuth() {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);

  const initializeAuth = useCallback(async () => {
    dispatch(setLoading(true));

    // First check if we have stored auth data
    const storedAuth = getAuthStorage();

    if (storedAuth?.org_access_token) {
      dispatch(
        setAuthData({
          name: storedAuth.name,
          org_access_token: storedAuth.org_access_token,
          user_refresh_token: storedAuth.user_refresh_token,
          selected_org_id: storedAuth.selected_org_id,
        })
      );
      return;
    }

    // Check for cookies from external login
    const { userToken, userRefreshToken } = getUserCookies();

    if (!userToken || !userRefreshToken) {
      // No auth cookies found, redirect to login
      dispatch(setLoading(false));
      redirectToLogin(window.location.pathname);
      return;
    }

    // Extract user email from JWT token
    const userEmail = getUserEmailFromToken(userToken);

    try {
      // Step 1: Fetch user's organisations using the user token
      const orgResponse = await fetchUserOrganisations(userToken);
      // Response structure: { _metadata, status, data: { organisations: [...] } }
      const organisations = orgResponse.data?.organisations || orgResponse.organisations || [];

      // Step 2: Filter for organisations with 'xelacore-admin' module
      const adminOrgs = organisations
        .filter((o) => (o.modules || []).includes('xelacore-admin'))
        .sort((a, b) => a.DepartmentName.localeCompare(b.DepartmentName));

      if (adminOrgs.length === 0) {
        dispatch(setError('You do not belong to any organisations with admin access'));
        clearUserCookies();
        return;
      }

      // Step 3: Use the first admin org to get an access token
      const adminOrg = adminOrgs[0];

      const orgAuthData = await setUserOrganisation({
        orgId: adminOrg.organisation_id,
        refreshToken: userRefreshToken,
      });

      if (orgAuthData?.access_token) {
        // Step 4: Store auth data with user email
        setAuthStorage({
          org_access_token: orgAuthData.access_token,
          name: userEmail,
          rememberMe: false,
          selected_org_id: adminOrg.organisation_id,
          user_refresh_token: orgAuthData.refresh_token || userRefreshToken,
        });

        dispatch(
          setAuthData({
            name: userEmail,
            org_access_token: orgAuthData.access_token,
            user_refresh_token: orgAuthData.refresh_token || userRefreshToken,
            selected_org_id: adminOrg.organisation_id,
          })
        );
      } else {
        throw new Error('Failed to get access token');
      }
    } catch (err) {
      const message = getApiErrorMessage(err, 'Authentication failed');
      dispatch(setError(message));
      clearUserCookies();
      redirectToLogin(window.location.pathname);
    }
  }, [dispatch]);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // Ignore logout errors
    } finally {
      clearUserCookies();
      dispatch(clearAuth());
      redirectToLogin();
    }
  }, [dispatch]);

  const isAuthenticated = auth.isAuthenticated;
  const isLoading = auth.isLoading;

  return {
    ...auth,
    isAuthenticated,
    isLoading,
    initializeAuth,
    logout,
  };
}

export function useAuthInit() {
  const { initializeAuth, isLoading, isAuthenticated } = useAuth();

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return { isLoading, isAuthenticated };
}
